import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { makeRequest } from "../assets/functions_helper";
import SendIcon from "@mui/icons-material/Send";
import { motion } from "framer-motion";

// Paleta de colores
const COLORS = {
  PRIMARY: "#2596be",
  PRIMARY_HOVER: "#1a7a9e",
  BACKGROUND: "#ffffff",
  PAPER_BG: "#ffffff",
  TEXT_PRIMARY: "#1E1E1E",
  TEXT_SECONDARY: "#6A6A6A",
  BORDER: "#e0e0e0",
  SHADOW: "rgba(37, 150, 190, 0.1)",
};

function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loadingToken, setLoadingToken] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [validToken, setValidToken] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });

  // Validar token al cargar
  useEffect(() => {
    setLoadingToken(true);
    makeRequest(
      "GET",
      `users/validate_reset_key/${token}`,
      {},
      "application/json",
      null,
      null,
      null,
      null,
      () => {} // no redirect
    )
      .then(() => {
        setValidToken(true);
        setSnackbar({
          open: true,
          message: "Token válido. Ingresa tu nueva contraseña.",
          severity: "success",
        });
      })
      .catch((error) => {
        console.error("Error validando token:", error);
        setValidToken(false);
        setSnackbar({
          open: true,
          message: "El enlace de recuperación no es válido o ha expirado.",
          severity: "error",
        });
      })
      .finally(() => setLoadingToken(false));
  }, [token]);

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validaciones
    if (password !== confirmPassword) {
      setSnackbar({ open: true, message: "Las contraseñas no coinciden", severity: "warning" });
      return;
    }
    if (password.length < 6) {
      setSnackbar({ open: true, message: "La contraseña debe tener al menos 6 caracteres", severity: "warning" });
      return;
    }

    setSubmitting(true);
    makeRequest(
      "POST",
      `users/update_password/${token}`,
      { new_password: password },
      "application/json",
      null,
      null,
      null,
      null,
      () => {}
    )
      .then(() => {
        setResetSuccess(true);
        setSnackbar({
          open: true,
          message: "Contraseña actualizada correctamente. Redirigiendo al login...",
          severity: "success",
        });
        setTimeout(() => navigate("/login"), 3000);
      })
      .catch((error) => {
        console.error("Error actualizando contraseña:", error);
        setSnackbar({
          open: true,
          message: "Error al actualizar la contraseña. Intenta de nuevo.",
          severity: "error",
        });
      })
      .finally(() => setSubmitting(false));
  };

  // Pantalla de carga mientras valida token
  if (loadingToken) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          bgcolor: COLORS.BACKGROUND,
        }}
      >
        <CircularProgress sx={{ color: COLORS.PRIMARY }} />
      </Box>
    );
  }

  // Token inválido
  if (!validToken && !resetSuccess) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          bgcolor: COLORS.BACKGROUND,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 4,
            maxWidth: 400,
            textAlign: "center",
            borderRadius: 2,
            border: `1px solid ${COLORS.BORDER}`,
            boxShadow: `0px 4px 12px ${COLORS.SHADOW}`,
          }}
        >
          <Typography variant="h5" gutterBottom fontWeight="bold" color="error">
            Enlace inválido
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, color: COLORS.TEXT_SECONDARY }}>
            El enlace para restablecer la contraseña no es válido o ha expirado. Por favor, solicita uno nuevo.
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate("/forgot-password")}
            sx={{
              backgroundColor: COLORS.PRIMARY,
              color: "white",
              "&:hover": { backgroundColor: COLORS.PRIMARY_HOVER },
            }}
          >
            Solicitar nuevo enlace
          </Button>
        </Paper>
      </Box>
    );
  }

  // Éxito
  if (resetSuccess) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          bgcolor: COLORS.BACKGROUND,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 4,
            maxWidth: 400,
            textAlign: "center",
            borderRadius: 2,
            border: `1px solid ${COLORS.BORDER}`,
            boxShadow: `0px 4px 12px ${COLORS.SHADOW}`,
          }}
        >
          <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ color: COLORS.PRIMARY }}>
            ¡Contraseña actualizada!
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, color: COLORS.TEXT_SECONDARY }}>
            Tu contraseña se ha cambiado exitosamente. Serás redirigido al inicio de sesión en unos segundos.
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate("/login")}
            sx={{
              backgroundColor: COLORS.PRIMARY,
              color: "white",
              "&:hover": { backgroundColor: COLORS.PRIMARY_HOVER },
            }}
            startIcon={<SendIcon />}
          >
            Ir a iniciar sesión
          </Button>
        </Paper>
      </Box>
    );
  }

  // Formulario principal (token válido)
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        bgcolor: COLORS.BACKGROUND,
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        style={{ width: "100%", maxWidth: 450 }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 2,
            border: `1px solid ${COLORS.BORDER}`,
            boxShadow: `0px 4px 12px ${COLORS.SHADOW}`,
          }}
        >
          <Typography variant="h5" align="center" gutterBottom fontWeight="bold" sx={{ color: COLORS.TEXT_PRIMARY }}>
            Restablecer contraseña
          </Typography>
          <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 3 }}>
            Ingresa tu nueva contraseña a continuación.
          </Typography>

          <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            <TextField
              label="Nueva contraseña"
              type="password"
              variant="outlined"
              fullWidth
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={submitting}
              size="small"
            />
            <TextField
              label="Confirmar contraseña"
              type="password"
              variant="outlined"
              fullWidth
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              disabled={submitting}
              size="small"
            />
            <Button
              type="submit"
              variant="contained"
              disabled={submitting}
              sx={{
                backgroundColor: COLORS.PRIMARY,
                color: "white",
                py: 1.5,
                "&:hover": { backgroundColor: COLORS.PRIMARY_HOVER },
                "&:disabled": { opacity: 0.6 },
              }}
              startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
            >
              {submitting ? "Actualizando..." : "Actualizar contraseña"}
            </Button>
          </Box>
        </Paper>
      </motion.div>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ fontFamily: "Como, sans-serif" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default ResetPassword;